import Image from '@creator/ui/components/Image/Image';
import LoadingSkeleton from '@creator/ui/components/LoadingSkeleton/LoadingSkeleton';
import { cn } from '@creator/ui/utils/ui';
import Link from '@src/basic-components/Link/Link';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { ensurePostUrl, isPostDeleted } from '@src/model/upvote/helpers';
import { usePostImage } from '@src/store/posts/hooks/usePostImage';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { type FC, memo, type ReactNode, useMemo } from 'react';
import PostByline from '../PostByline';
import PostMenuPopover from '../PostMenuPopover/PostMenuPopover';
import PostStatsAndReactions from '../PostStatsAndReactions/PostStatsAndReactions';
import PostTitle from './PostTitle';

export interface PostCardProps {
    className?: string;
    tokenName: string;
    fallbackImage?: string;
    postId: string;
    children?: ReactNode;
    showMainTextContent?: boolean;
    isLoading?: boolean;
    disableMenuPopover?: boolean;
}

const PostCard: FC<PostCardProps> = props => {
    const { tokenName, postId, className, isLoading } = props;

    const { url: postImageUrl } = usePostImage({ postId, imageType: 'featured' });
    const url = useMemo(() => `/${ensureTokenUrl(tokenName)}/${ensurePostUrl(postId)}`, [postId, tokenName]);

    const renderPostCardFooter = () => {
        return <PostStatsAndReactions className="h-5 space-x-4 text-white lg:space-x-5" tokenName={tokenName} isDisabled={isPostDeleted(postId)} postId={postId} />;
    };

    function renderByline() {
        if (isLoading) return <LoadingSkeleton className="w-20 h-3" />;
        return <PostByline className="text-white" postId={postId} tokenName={tokenName} />;
    }

    function renderMainImage() {
        const { fallbackImage } = props;
        return (
            <Link to={url} className="block">
                <Image className="overflow-hidden aspect-1/1" src={postImageUrl || fallbackImage} fallbackSrc={fallbackImage} isLoading={isLoading} />
            </Link>
        );
    }

    function renderPostMenuPopover() {
        const { disableMenuPopover } = props;
        if (disableMenuPopover) return null;
        return <PostMenuPopover className="text-white" postId={postId} tokenName={tokenName} />;
    }

    return (
        <div className={cn('relative flex flex-col cursor-pointer', isPostDeleted(postId) ? 'opacity-40' : '', className)}>
            {renderMainImage()}
            <div className="absolute bottom-0 left-0 right-0 flex flex-col justify-end h-32 p-3 space-y-2 bg-gradient-to-t from-black via-black/60 to-transparent">
                <div className="space-y-2">
                    <Link to={url} className="block">
                        <PostTitle className="text-base text-white" as="h2" postId={postId} tokenName={tokenName} />
                    </Link>
                    {renderByline()}
                </div>
                <div className="flex items-start justify-between">
                    {renderPostCardFooter()}
                    {renderPostMenuPopover()}
                </div>
            </div>
        </div>
    );
};

export default memo(PostCard, propsEqualityFn);
